import React, { useState, useEffect, useCallback } from "react";
import S from "../styles";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

export default function ReverbPage({ store }) {
  const [listings, setListings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [syncLog, setSyncLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [tab, setTab] = useState("orders"); // orders | listings | sync | settings
  const [tokenInfo, setTokenInfo] = useState({ hasToken: false, masked: "" });
  const [tokenInput, setTokenInput] = useState("");
  const [savingToken, setSavingToken] = useState(false);
  const [editingCell, setEditingCell] = useState(null); // { listingId, field, value }
  const [selected, setSelected] = useState(new Set());
  const [batchSku, setBatchSku] = useState("");
  const [batchUpdating, setBatchUpdating] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [labelLinks, setLabelLinks] = useState(null); // [{title, url}] for batch label modal

  const fetchTokenInfo = useCallback(() => {
    fetch(`${API_URL}/api/settings/reverb-token`)
      .then((r) => r.json())
      .then(setTokenInfo)
      .catch(() => {});
  }, []);

  const fetchListings = useCallback(() => {
    setLoading(true);
    fetch(`${API_URL}/api/reverb/listings`)
      .then((r) => r.json())
      .then((data) => {
        if (data.error) setError(data.error);
        else if (data.listings) setListings(data.listings);
        setLoading(false);
      })
      .catch((err) => { setError(`Listings fetch failed: ${err.message}`); setLoading(false); });
  }, []);

  const fetchOrders = useCallback(() => {
    fetch(`${API_URL}/api/reverb/orders`)
      .then((r) => r.json())
      .then((data) => {
        if (data.orders) setOrders(data.orders);
      })
      .catch(() => {});
  }, []);

  const fetchSyncLog = useCallback(() => {
    fetch(`${API_URL}/api/reverb/sync-log`)
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setSyncLog(data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchTokenInfo();
  }, [fetchTokenInfo]);

  useEffect(() => {
    if (tokenInfo.hasToken) {
      if (tab === "listings") fetchListings();
      if (tab === "orders") { fetchOrders(); fetchSyncLog(); }
      if (tab === "sync") fetchSyncLog();
    }
  }, [tokenInfo.hasToken, tab, fetchListings, fetchOrders, fetchSyncLog]);

  const [error, setError] = useState("");

  const saveToken = () => {
    setSavingToken(true);
    setError("");
    fetch(`${API_URL}/api/settings/reverb-token`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: tokenInput }),
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Server returned ${r.status}`);
        return r.json();
      })
      .then(() => {
        setTokenInput("");
        fetchTokenInfo();
        setSavingToken(false);
      })
      .catch((err) => {
        setError(`Failed to save token: ${err.message}`);
        setSavingToken(false);
      });
  };

  const handleSync = () => {
    setSyncing(true);
    setSyncResult(null);
    fetch(`${API_URL}/api/reverb/sync-inventory`, { method: "POST" })
      .then((r) => r.json())
      .then((data) => {
        setSyncResult(data);
        setSyncing(false);
        fetchSyncLog();
      })
      .catch(() => setSyncing(false));
  };

  const saveField = (listingId, field, value) => {
    // Map field names to Reverb API payload keys
    const payload = {};
    if (field === "sku") payload.sku = value;
    else if (field === "price") payload.price = { amount: value, currency: "USD" };
    else if (field === "inventory") { payload.has_inventory = true; payload.inventory = Number(value); }
    else if (field === "title") payload.title = value;

    fetch(`${API_URL}/api/reverb/listings/${listingId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((r) => r.json())
      .then(() => {
        setEditingCell(null);
        // Update local state
        setListings((prev) =>
          prev.map((l) => {
            if (String(l.id) !== String(listingId)) return l;
            if (field === "sku") return { ...l, sku: value };
            if (field === "price") return { ...l, price: { ...(l.price || {}), amount: value } };
            if (field === "inventory") return { ...l, inventory: Number(value) };
            if (field === "title") return { ...l, title: value };
            return l;
          })
        );
      })
      .catch(() => {});
  };

  const handleBatchSku = () => {
    if (selected.size === 0 || !batchSku.trim()) return;
    setBatchUpdating(true);
    const updates = Array.from(selected).map((lid) => ({ listingId: lid, sku: batchSku.trim() }));
    fetch(`${API_URL}/api/reverb/listings/batch-sku`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ updates }),
    })
      .then((r) => r.json())
      .then(() => {
        setBatchUpdating(false);
        setSelected(new Set());
        setBatchSku("");
        fetchListings();
      })
      .catch(() => setBatchUpdating(false));
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filteredListings.length) setSelected(new Set());
    else setSelected(new Set(filteredListings.map((l) => String(l.id))));
  };

  // Stats
  const activeListings = listings.filter((l) => l.state?.slug === "live" || l.state?.slug === "published" || !l.state);
  const draftListings = listings.filter((l) => l.state?.slug === "draft");
  const soldListings = listings.filter((l) => l.state?.slug === "sold" || l.state?.slug === "ended");

  const filteredListings = listings.filter((l) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (l.title || "").toLowerCase().includes(s) || (l.sku || "").toLowerCase().includes(s);
  });

  const tabs = [
    { key: "orders", label: "Orders" },
    { key: "listings", label: "Listings" },
    { key: "sync", label: "Sync Log" },
    { key: "settings", label: "Settings" },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 32, fontWeight: 900, color: "#0A0A0F", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1 }}>Reverb Integration</h2>
        {tokenInfo.hasToken && tab !== "settings" && (
          <button onClick={handleSync} disabled={syncing} style={{ ...S.addBtn, opacity: syncing ? 0.6 : 1 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
            </svg>
            {syncing ? "Syncing..." : "Sync Sales"}
          </button>
        )}
      </div>

      {/* Tab nav */}
      <div style={{ ...S.nav, marginBottom: 20 }}>
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{ ...S.navBtn, ...(tab === t.key ? S.navBtnActive : {}) }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Error banner */}
      {error && tab !== "settings" && (
        <div style={{ marginBottom: 16, padding: "10px 14px", background: "rgba(239,68,68,0.1)", border: "1px solid #ef4444", borderRadius: 0, fontSize: 14, color: "#ef4444", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{error}</span>
          <button onClick={() => setError("")} style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 16 }}>x</button>
        </div>
      )}

      {/* Sync result toast */}
      {syncResult && (
        <div style={{ background: "rgba(34,197,94,0.1)", border: "1px solid #22c55e", borderRadius: 0, padding: "12px 16px", marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#34d399", marginBottom: 4 }}>
            Sync Complete
          </div>
          <div style={{ fontSize: 14, color: "#0A0A0F" }}>
            {syncResult.synced?.length || 0} orders synced, {syncResult.skipped || 0} already synced, {syncResult.errors?.length || 0} errors
          </div>
          {syncResult.errors?.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {syncResult.errors.map((e, i) => (
                <div key={i} style={{ fontSize: 13, color: "#ef4444" }}>
                  Order {e.orderId}: {e.reason} {e.sku ? `(SKU: ${e.sku})` : ""} — {e.title}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Settings Tab */}
      {tab === "settings" && (
        <div style={{ ...S.lineCard, padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)", marginBottom: 12 }}>Reverb API Token</div>
          <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 16, lineHeight: 1.5 }}>
            Generate a token at Reverb: My Account &rarr; API & Integrations &rarr; Generate New Token.
            Scopes needed: read_listings, write_listings, read_orders.
          </p>
          {tokenInfo.hasToken && (
            <div style={{ marginBottom: 12, padding: "10px 14px", background: "#FFFFFF", borderRadius: 0, border: "2px solid #0A0A0F" }}>
              <span style={{ fontSize: 13, color: "rgba(10,10,15,0.58)" }}>Current token: </span>
              <span style={{ fontSize: 14, fontFamily: "'JetBrains Mono', monospace", color: "#34d399" }}>{tokenInfo.masked}</span>
            </div>
          )}
          <div style={{ display: "flex", gap: 10 }}>
            <input
              type="password"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              placeholder={tokenInfo.hasToken ? "Enter new token to replace..." : "Paste your Reverb API token..."}
              style={{ ...S.input, flex: 1 }}
            />
            <button onClick={saveToken} disabled={!tokenInput.trim() || savingToken} style={{ ...S.addBtn, opacity: !tokenInput.trim() || savingToken ? 0.5 : 1 }}>
              {savingToken ? "Saving..." : "Save Token"}
            </button>
          </div>
          {error && (
            <div style={{ marginTop: 12, padding: "10px 14px", background: "rgba(239,68,68,0.1)", border: "1px solid #ef4444", borderRadius: 0, fontSize: 14, color: "#ef4444" }}>
              {error}
            </div>
          )}
        </div>
      )}

      {/* No token warning */}
      {!tokenInfo.hasToken && tab !== "settings" && (
        <div style={{ ...S.lineCard, padding: 24, textAlign: "center" }}>
          <div style={{ fontSize: 16, color: "rgba(10,10,15,0.58)", marginBottom: 8 }}>No Reverb API token configured</div>
          <button onClick={() => setTab("settings")} style={S.addBtn}>Configure Token</button>
        </div>
      )}

      {/* Listings Tab */}
      {tab === "listings" && tokenInfo.hasToken && (
        <>
          {/* Stats */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
            <div style={S.statCard}>
              <div style={S.statLabel}>Total</div>
              <div style={S.statValue}>{listings.length}</div>
            </div>
            <div style={S.statCard}>
              <div style={S.statLabel}>Active</div>
              <div style={{ ...S.statValue, color: "#34d399" }}>{activeListings.length}</div>
            </div>
            <div style={S.statCard}>
              <div style={S.statLabel}>Draft</div>
              <div style={{ ...S.statValue, color: "rgba(10,10,15,0.58)" }}>{draftListings.length}</div>
            </div>
            <div style={S.statCard}>
              <div style={S.statLabel}>Sold/Ended</div>
              <div style={{ ...S.statValue, color: "#0033FF" }}>{soldListings.length}</div>
            </div>
          </div>

          {/* Search + batch bar */}
          <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center" }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search listings..."
              style={{ ...S.input, flex: 1, maxWidth: 300 }}
            />
            {selected.size > 0 && (
              <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "6px 12px", background: "#FFFFFF", borderRadius: 0, border: "2px solid #0A0A0F" }}>
                <span style={{ fontSize: 13, color: "#0033FF", fontWeight: 600 }}>{selected.size} selected</span>
                <input
                  value={batchSku}
                  onChange={(e) => setBatchSku(e.target.value)}
                  placeholder="New SKU..."
                  style={{ ...S.input, width: 140, padding: "6px 10px", fontSize: 14 }}
                />
                <button onClick={handleBatchSku} disabled={!batchSku.trim() || batchUpdating} style={{ ...S.addBtn, padding: "6px 12px", fontSize: 13, opacity: !batchSku.trim() || batchUpdating ? 0.5 : 1 }}>
                  {batchUpdating ? "Updating..." : "Set SKU"}
                </button>
                <button onClick={() => setSelected(new Set())} style={{ ...S.clearBtn, padding: "6px 10px", fontSize: 13 }}>Clear</button>
              </div>
            )}
            <button onClick={fetchListings} disabled={loading} style={{ ...S.iconBtn, opacity: loading ? 0.5 : 1 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
              </svg>
            </button>
          </div>

          {loading ? (
            <div style={{ textAlign: "center", padding: 40, color: "rgba(10,10,15,0.58)" }}>Loading listings from Reverb...</div>
          ) : (
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={{ ...S.th, width: 40 }}>
                      <input type="checkbox" checked={selected.size === filteredListings.length && filteredListings.length > 0} onChange={toggleAll} />
                    </th>
                    <th style={S.th}>Title</th>
                    <th style={S.th}>SKU</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Price</th>
                    <th style={S.th}>Condition</th>
                    <th style={{ ...S.th, textAlign: "center" }}>Qty</th>
                    <th style={S.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredListings.map((l) => {
                    const lid = String(l.id);
                    const price = l.price?.amount || l.price || "";
                    const condition = l.condition?.display_name || l.condition || "";
                    const status = l.state?.description || l.state?.slug || "";
                    const statusSlug = l.state?.slug || "";
                    const statusColor = statusSlug === "live" || statusSlug === "published" ? "#34d399" : statusSlug === "draft" ? "rgba(10,10,15,0.58)" : "#0033FF";
                    const inv = typeof l.inventory === "number" ? l.inventory : "";

                    const isEditing = (field) => editingCell?.listingId === lid && editingCell?.field === field;
                    const startEdit = (field, value) => setEditingCell({ listingId: lid, field, value: String(value ?? "") });
                    const editKeyDown = (e, field) => {
                      if (e.key === "Enter") saveField(lid, field, editingCell.value);
                      if (e.key === "Escape") setEditingCell(null);
                    };
                    const checkIcon = (field) => (
                      <button onClick={() => saveField(lid, field, editingCell.value)} style={S.iconBtn}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                      </button>
                    );

                    return (
                      <tr key={lid} style={selected.has(lid) ? S.editingRow : {}}>
                        <td style={S.td}>
                          <input type="checkbox" checked={selected.has(lid)} onChange={() => toggleSelect(lid)} />
                        </td>
                        {/* Title — editable */}
                        <td style={{ ...S.td, maxWidth: 300 }}>
                          {isEditing("title") ? (
                            <div style={{ display: "flex", gap: 4 }}>
                              <input
                                autoFocus
                                value={editingCell.value}
                                onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
                                onKeyDown={(e) => editKeyDown(e, "title")}
                                style={{ ...S.cellInput, width: "100%" }}
                              />
                              {checkIcon("title")}
                            </div>
                          ) : (
                            <div
                              onClick={() => startEdit("title", l.title || "")}
                              style={{ fontSize: 14, fontWeight: 500, color: "#0A0A0F", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", cursor: "pointer", borderBottom: "1px dashed transparent" }}
                              onMouseEnter={(e) => { e.currentTarget.style.borderBottomColor = "#0A0A0F"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.borderBottomColor = "transparent"; }}
                            >
                              {l.title || "—"}
                            </div>
                          )}
                        </td>
                        {/* SKU — editable */}
                        <td style={S.td}>
                          {isEditing("sku") ? (
                            <div style={{ display: "flex", gap: 4 }}>
                              <input
                                autoFocus
                                value={editingCell.value}
                                onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
                                onKeyDown={(e) => editKeyDown(e, "sku")}
                                style={{ ...S.cellInput, width: 120 }}
                              />
                              {checkIcon("sku")}
                            </div>
                          ) : (
                            <span
                              onClick={() => startEdit("sku", l.sku || "")}
                              style={{ cursor: "pointer", fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: l.sku ? "#0A0A0F" : "rgba(10,10,15,0.58)", borderBottom: "1px dashed 0A0A0F", paddingBottom: 1 }}
                            >
                              {l.sku || "—"}
                            </span>
                          )}
                        </td>
                        {/* Price — editable */}
                        <td style={{ ...S.td, textAlign: "right" }}>
                          {isEditing("price") ? (
                            <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                              <input
                                autoFocus
                                type="number"
                                step="0.01"
                                value={editingCell.value}
                                onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
                                onKeyDown={(e) => editKeyDown(e, "price")}
                                style={{ ...S.cellInput, width: 90, textAlign: "right" }}
                              />
                              {checkIcon("price")}
                            </div>
                          ) : (
                            <span
                              onClick={() => startEdit("price", price)}
                              style={{ cursor: "pointer", fontFamily: "'JetBrains Mono', monospace", color: "#0033FF", borderBottom: "1px dashed 0A0A0F", paddingBottom: 1 }}
                            >
                              {price ? `$${Number(price).toFixed(2)}` : "—"}
                            </span>
                          )}
                        </td>
                        <td style={S.td}>
                          <span style={{ fontSize: 13, color: "#0A0A0F" }}>{condition || "—"}</span>
                        </td>
                        {/* Inventory — editable */}
                        <td style={{ ...S.td, textAlign: "center" }}>
                          {isEditing("inventory") ? (
                            <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                              <input
                                autoFocus
                                type="number"
                                value={editingCell.value}
                                onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
                                onKeyDown={(e) => editKeyDown(e, "inventory")}
                                style={{ ...S.cellInput, width: 60, textAlign: "center" }}
                              />
                              {checkIcon("inventory")}
                            </div>
                          ) : (
                            <span
                              onClick={() => startEdit("inventory", inv)}
                              style={{ cursor: "pointer", fontFamily: "'JetBrains Mono', monospace", borderBottom: "1px dashed 0A0A0F", paddingBottom: 1 }}
                            >
                              {inv !== "" ? inv : "—"}
                            </span>
                          )}
                        </td>
                        <td style={S.td}>
                          <span style={{ ...S.typeBadge, color: statusColor, fontSize: 12 }}>{status || "—"}</span>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredListings.length === 0 && (
                    <tr><td colSpan={7} style={{ ...S.td, textAlign: "center", color: "rgba(10,10,15,0.58)", padding: 24 }}>No listings found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Orders Tab */}
      {tab === "orders" && tokenInfo.hasToken && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
            <div style={{ fontSize: 14, color: "rgba(10,10,15,0.58)" }}>{orders.length} orders</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {selectedOrders.size > 0 && (
                <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "6px 12px", background: "#FFFFFF", borderRadius: 0, border: "2px solid #0A0A0F" }}>
                  <span style={{ fontSize: 13, color: "#0033FF", fontWeight: 600 }}>{selectedOrders.size} selected</span>
                  <button
                    onClick={() => {
                      const links = orders
                        .filter((o) => selectedOrders.has(String(o.order_number || o.id)))
                        .map((o) => ({
                          orderId: String(o.order_number || o.id),
                          title: o.title || o.listing?.title || "",
                          url: o._links?.shipping_label?.href || "",
                        }))
                        .filter((l) => l.url);
                      if (links.length > 0) setLabelLinks(links);
                    }}
                    style={{ ...S.addBtn, padding: "6px 12px", fontSize: 13 }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Open Labels ({selectedOrders.size})
                  </button>
                  <button onClick={() => setSelectedOrders(new Set())} style={{ ...S.clearBtn, padding: "6px 10px", fontSize: 13 }}>Clear</button>
                </div>
              )}
              <button onClick={fetchOrders} style={S.iconBtn}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                  <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
                </svg>
              </button>
            </div>
          </div>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={{ ...S.th, width: 40 }}>
                    <input
                      type="checkbox"
                      checked={selectedOrders.size === orders.length && orders.length > 0}
                      onChange={() => {
                        if (selectedOrders.size === orders.length) setSelectedOrders(new Set());
                        else setSelectedOrders(new Set(orders.map((o) => String(o.order_number || o.id))));
                      }}
                    />
                  </th>
                  <th style={S.th}>Order #</th>
                  <th style={S.th}>Title</th>
                  <th style={S.th}>SKU</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Amount</th>
                  <th style={S.th}>Status</th>
                  <th style={S.th}>Shipping</th>
                  <th style={{ ...S.th, textAlign: "center" }}>Qty Deducted</th>
                  <th style={S.th}>Date</th>
                  <th style={{ ...S.th, textAlign: "center" }}>Label</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => {
                  const orderId = String(o.order_number || o.id);
                  const listing = o.listing || {};
                  const sku = o.sku || listing.sku || "";
                  const title = o.title || listing.title || "";
                  const amount = o.amount_product?.amount || o.total?.amount || "";
                  const status = o.status || "";
                  const date = o.created_at || o.paid_at || "";
                  const dateStr = date ? new Date(date).toLocaleDateString() : "";
                  const statusColor = status === "paid" || status === "shipped" ? "#34d399" : status === "cancelled" || status === "refunded" ? "#ef4444" : "#0A0A0F";
                  const labelUrl = o._links?.shipping_label?.href || "";
                  return (
                    <tr key={orderId} style={selectedOrders.has(orderId) ? S.editingRow : {}}>
                      <td style={S.td}>
                        <input
                          type="checkbox"
                          checked={selectedOrders.has(orderId)}
                          onChange={() => {
                            setSelectedOrders((prev) => {
                              const next = new Set(prev);
                              if (next.has(orderId)) next.delete(orderId);
                              else next.add(orderId);
                              return next;
                            });
                          }}
                        />
                      </td>
                      <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}>{orderId}</td>
                      <td style={{ ...S.td, maxWidth: 250 }}>
                        <div style={{ fontSize: 14, color: "#0A0A0F", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</div>
                      </td>
                      <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: sku ? "#0A0A0F" : "rgba(10,10,15,0.58)" }}>{sku || "—"}</td>
                      <td style={{ ...S.td, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "#0033FF" }}>
                        {amount ? `$${Number(amount).toFixed(2)}` : "—"}
                      </td>
                      <td style={S.td}>
                        <span style={{ ...S.typeBadge, color: statusColor, fontSize: 12 }}>{status}</span>
                      </td>
                      <td style={S.td}>
                        {(() => {
                          const ss = o.shipment_status || "";
                          const shipLabel = ss.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) || "—";
                          const shipColor = ss === "delivered" ? "#34d399" : ss === "in_transit" ? "#60a5fa" : ss === "pre_transit" ? "#0033FF" : ss === "return_to_sender" ? "#ef4444" : "rgba(10,10,15,0.58)";
                          return <span style={{ ...S.typeBadge, color: shipColor, fontSize: 12 }}>{shipLabel}</span>;
                        })()}
                      </td>
                      {(() => {
                        const synced = syncLog.find((s) => String(s.reverbOrderId) === orderId);
                        const qty = synced?.quantityDeducted || 0;
                        return (
                          <td style={{ ...S.td, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", color: qty > 0 ? "#34d399" : "rgba(10,10,15,0.58)" }}>
                            {qty > 0 ? `-${qty}` : "—"}
                          </td>
                        );
                      })()}
                      <td style={{ ...S.td, fontSize: 13, color: "rgba(10,10,15,0.58)" }}>{dateStr}</td>
                      <td style={{ ...S.td, textAlign: "center" }}>
                        {labelUrl ? (
                          <a
                            href={labelUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 0, border: "2px solid #0A0A0F", background: "transparent", color: "#0033FF", fontSize: 12, fontWeight: 600, textDecoration: "none", cursor: "pointer", fontFamily: "'Archivo', sans-serif" }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                            Label
                          </a>
                        ) : (
                          <span style={{ fontSize: 12, color: "rgba(10,10,15,0.58)" }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {orders.length === 0 && (
                  <tr><td colSpan={11} style={{ ...S.td, textAlign: "center", color: "rgba(10,10,15,0.58)", padding: 24 }}>No orders found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Label Links Modal */}
      {labelLinks && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={() => setLabelLinks(null)} style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)" }} />
          <div style={{ position: "relative", width: "100%", maxWidth: 520, background: "#F4F2EC", border: "2px solid #0A0A0F", borderRadius: 0, overflow: "hidden", zIndex: 301, maxHeight: "80vh", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "16px 20px", background: "#FFFFFF", borderBottom: "2px solid #0A0A0F", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: "#0A0A0F" }}>Shipping Labels ({labelLinks.length})</span>
              <button onClick={() => setLabelLinks(null)} style={{ background: "none", border: "2px solid #0A0A0F", borderRadius: 0, padding: "4px 8px", cursor: "pointer", color: "rgba(10,10,15,0.58)", display: "flex", alignItems: "center" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div style={{ padding: "12px 20px 8px", fontSize: 13, color: "rgba(10,10,15,0.58)" }}>
              Click each link to open — or <span style={{ color: "#0033FF" }}>Cmd+click</span> to open in background tab.
            </div>
            <div style={{ padding: "8px 20px 20px", overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
              {labelLinks.map((l, i) => (
                <a
                  key={i}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
                    background: "#FFFFFF", borderRadius: 0, border: "2px solid #0A0A0F",
                    textDecoration: "none", transition: "border-color .15s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#0033FF"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#0A0A0F"; }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0033FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#0A0A0F", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      Order #{l.orderId}
                    </div>
                    <div style={{ fontSize: 12, color: "rgba(10,10,15,0.58)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.title}</div>
                  </div>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(10,10,15,0.58)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Sync Log Tab */}
      {tab === "sync" && tokenInfo.hasToken && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ fontSize: 14, color: "rgba(10,10,15,0.58)" }}>{syncLog.length} synced orders</div>
            <button onClick={fetchSyncLog} style={S.iconBtn}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
              </svg>
            </button>
          </div>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Order ID</th>
                  <th style={S.th}>Title</th>
                  <th style={S.th}>SKU</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Amount</th>
                  <th style={{ ...S.th, textAlign: "center" }}>Qty Deducted</th>
                  <th style={S.th}>Synced At</th>
                </tr>
              </thead>
              <tbody>
                {syncLog.map((l) => (
                  <tr key={l.id}>
                    <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}>{l.reverbOrderId}</td>
                    <td style={{ ...S.td, maxWidth: 250 }}>
                      <div style={{ fontSize: 14, color: "#0A0A0F", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.orderTitle}</div>
                    </td>
                    <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}>{l.sku || "—"}</td>
                    <td style={{ ...S.td, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "#0033FF" }}>
                      {l.orderAmount ? `$${Number(l.orderAmount).toFixed(2)}` : "—"}
                    </td>
                    <td style={{ ...S.td, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", color: l.quantityDeducted > 0 ? "#34d399" : "rgba(10,10,15,0.58)" }}>
                      {l.quantityDeducted > 0 ? `-${l.quantityDeducted}` : "0"}
                    </td>
                    <td style={{ ...S.td, fontSize: 13, color: "rgba(10,10,15,0.58)" }}>
                      {l.syncedAt ? new Date(l.syncedAt).toLocaleString() : "—"}
                    </td>
                  </tr>
                ))}
                {syncLog.length === 0 && (
                  <tr><td colSpan={6} style={{ ...S.td, textAlign: "center", color: "rgba(10,10,15,0.58)", padding: 24 }}>No sync history yet. Click "Sync Sales" to start.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
